import {  useState, useEffect } from 'react'
import CustomizedAccordions from '../../../../components/common/accordion'
import FormikContext from '../../../../context/formikContext'
import RadioGroup from '@mui/material/RadioGroup'
import { useFormik } from 'formik'
import RadioButton from '../../../../components/common/fields/radioButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Loader from '../../../../components/common/loader'
import { FormationOtherDatesType } from '../../../../types/api/formations'
import { getDetailedDate } from '../../../../utils'
import { useUpdateFormationDate } from '../../../../hooks/api/formations'

const getFormationTypeLabel = (type: string | undefined): string => {
  if (!type) return ''
  switch (type.toUpperCase()) {
    case 'EC':
      return 'En classe'
    case 'EL':
      return 'En ligne'
    default:
      return type
  }
}

interface Props {
  status: string
  dataDates: FormationOtherDatesType[] | undefined
  cancelFn: () => void
  oldTrainingId: string
  isRefreshed: () => void
  isRefreshedData: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
  onFormReady?: (submitFn: () => void) => void
  onSuccess?: () => void
}

const UpdateDateInscription = ({
  status,
  dataDates,
  cancelFn,
  oldTrainingId,
  isRefreshed,
  isRefreshedData,
  onSubmittingChange,
  onFormReady,
  onSuccess,
}: Props) => {
  const [value, setValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value
    setValue(newValue)
  }
  
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      old_training_id: oldTrainingId,
      new_training_id: value,
    },
    onSubmit: async () => {
      const submissionData = {
        old_training_id: oldTrainingId,
        new_training_id: value,
      }
      
      if (!submissionData.old_training_id || !submissionData.new_training_id) {
        return
      }
      
      setIsSubmitting(true)
      onSubmittingChange?.(true)
      await updateInscriptionDate(
        submissionData,
        {
          onSuccess: async () => {
            isRefreshed()
            isRefreshedData()
            onSuccess?.()
          },
          onSettled: async () => {
            setIsSubmitting(false)
            onSubmittingChange?.(false)
          },
        },
      )
    },
  })
  const { mutate: updateInscriptionDate } = useUpdateFormationDate(formik)

  useEffect(() => {
    onFormReady?.(formik.handleSubmit)
  }, [formik.handleSubmit, onFormReady])

  return status === 'loading' || isSubmitting ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        mt: '10px',
      }}
    >
      <Loader />
    </Box>
  ) : dataDates ? (
    <Box>
      <Box
        sx={{
          flex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '16px',
          paddingBottom: '24px',
        }}
      >
        <Typography variant='captionSmall' sx={{ marginBottom: '8px', textTransform: 'uppercase' }}>
          {dataDates[0]?.cohort ? ` – ${dataDates[0]?.cohort}` : null}
          {dataDates[0]?.title}
        </Typography>
        <Typography variant='h4'>Changement de dates</Typography>
        <Typography variant='body1'>
          Modifiez votre inscription à cette formation pour une des dates ci-dessous:{' '}
        </Typography>
      </Box>
      {
        <FormikContext.Provider value={formik}>
          {dataDates.length > 0 ? (
            dataDates.map((date, index) => {
                if (date.schedule != null && date.schedule.length >= 1) {
                  return (
                    <Box
                      key={`date-with-schedule-${date.id}-${index}`}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        padding: '12px 0',
                        borderBottom: '1px solid #DADCDD',
                        '&:last-child': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      <RadioGroup
                        aria-labelledby='controlled-radio-buttons-group'
                        name='grouope'
                        value={value}
                        onChange={handleChange}
                      >
                        <Box sx={{ 
                          '& .MuiAccordion-root': { 
                            margin: 0, 
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                          },
                          '& .MuiAccordionSummary-root': {
                            padding: 0,
                            minHeight: 'auto',
                          }
                        }}>
                          <CustomizedAccordions
                            panels={[
                              {
                                panel_id: `${date.id}-${index}`,
                                header: (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                    <RadioButton
                                      label={getDetailedDate(date.start_date, date.end_date)}
                                      value={date.id}
                                    />
                                    <Typography variant='captionSmall' sx={{ marginLeft: '28px', color: '#666' }}>
                                      {getFormationTypeLabel(date.type)}
                                      {date.acq_region?.name && ` – ${date.acq_region.name}`}
                                    </Typography>
                                  </Box>
                                ),
                                schedules: date.schedule,
                              },
                            ]}
                            info={date?.cohort}
                          />
                        </Box>
                      </RadioGroup>
                    </Box>
                  )
                } else {
                  return (
                    <Box
                      key={`date-simple-${date.id}-${index}`}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        padding: '12px 0',
                      }}
                    >
                      <RadioGroup
                        aria-labelledby='controlled-radio-buttons-group'
                        name='grouope'
                        value={value}
                        onChange={handleChange}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <RadioButton
                            label={getDetailedDate(date.start_date, date.end_date)}
                            value={date.id}
                          />
                          <Typography variant='captionSmall' sx={{ marginLeft: '28px', color: '#666' }}>
                            {getFormationTypeLabel(date.type)}
                            {date.acq_region?.name && ` – ${date.acq_region.name}`}
                          </Typography>
                        </Box>
                      </RadioGroup>
                    </Box>
                  )
                }
              })
          ) : (
            <div>Aucune date disponible</div>
          )}
        </FormikContext.Provider>
      }
      <Box sx={{ 
        marginTop: '24px', 
        padding: '16px', 
        backgroundColor: '#F5F5F5', 
        borderRadius: '4px',
        borderLeft: '4px solid #F9A51A'
      }}>
        <Typography variant='body2' sx={{ color: '#333' }}>
          D'autres dates d'inscription en classe ou en ligne pourraient être disponibles dans d'autres ACQ régionales, 
          veuillez contacter l'ACQ régionale avec qui vous avez acheté votre formation pour voir les possibilités qui vous sont offertes.
        </Typography>
      </Box>
    </Box>
  ) : (
    <div> Aucune date disponible</div>
  )
}

export default UpdateDateInscription
